/** Extracts the HTTP status code from an Axios error response or raw error object. */
export function getHttpStatus(err: any): number | undefined {
    return err?.response?.status ?? err?.status;
}

/** Extracts the request URL from an Axios config or raw error object. */
export function getHttpUrl(err: any): string | undefined {
    return err?.config?.url ?? err?.url;
}

/** Returns true for network errors and timeout messages that are expected noise and should not be sent to Sentry. */
export function isNetworkNoise(err: any): boolean {
    const msg = String(err?.message || '');
    if (!msg) return false;
    return (
        msg.includes('Network Error') ||
        msg.toLowerCase().includes('timeout') ||
        msg.includes('Connection timeout')
    );
}

/** Returns true when an Axios request was canceled (ERR_CANCELED / CanceledError), e.g. by TanStack Query on navigation. */
// Axios sets code='ERR_CANCELED' and name='CanceledError' when a request is aborted
// (e.g. TanStack Query cancels in-flight requests on navigation). This is expected behavior.
export function isCanceledError(err: any): boolean {
    return err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError';
}

/** Returns true for WebSocket closure and timeout messages that are expected noise and should not be sent to Sentry. */
export function isWebsocketNoise(err: any): boolean {
    const msg = String(err?.message || '');
    return msg.includes('Websocket was closed by user') || msg.includes('Connection timeout 20s');
}

/** Returns true for statuses that must be reported as an ERROR-level Sentry Issue: 422, any 5xx, or a missing status (thrown/network-level exception). */
export function captureAsError(status?: number): boolean {
    return status === 422 || status == null || status >= 500;
}

/** Returns true for all other 4xx statuses (400, 401, 403, 404, 409, 429, ...) — reported as a lightweight Sentry Log, not an Issue. */
export function captureAsEvent(status?: number): boolean {
    return status != null && status >= 400 && status < 500 && status !== 422;
}
